import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { aadScopes } from '.././auth-config';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, AuthenticationResult, InteractionStatus } from '@azure/msal-browser';
import { ConfigService } from '../services/config.service';
import { UserService } from '../services/user.service';
import { MenuItem } from '@siemens/element-ng/common';
import { SiThemeService, ThemeType } from '@siemens/element-ng/theme';
import { SiModalService } from '@siemens/element-ng/modal';
import { User } from '../models/user';
import { SecureTokenService } from '../services/secure-token.service';
import { AskAiDialogComponent } from '../pages/ask-ai-dialog/ask-ai-dialog.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HeaderComponent implements OnInit {
  loginDisplay = false;
  userData: User | undefined;
  version = '';
  accountItems: MenuItem[] = [{ title: 'Logout', icon: 'element-logout', link: 'logout' }];
  helpItems: MenuItem[] = [];
  isGlobalAdmin = false;
  private samlUserFetched = false;
  private themeItems: MenuItem[] = [
    { id: 'auto', title: 'THEME_EDITOR.COLOR_SCHEMES.AUTO', action: () => this.switchTheme('auto') },
    { id: 'light', title: 'THEME_EDITOR.COLOR_SCHEMES.LIGHT', action: () => this.switchTheme('light') },
    { id: 'dark', title: 'THEME_EDITOR.COLOR_SCHEMES.DARK', action: () => this.switchTheme('dark') },
  ];

  private currentTheme = this.themeService.resolvedColorScheme;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private configService: ConfigService,
    private userService: UserService,
    private themeService: SiThemeService,
    private cdr: ChangeDetectorRef,
    private secureTokenService: SecureTokenService,
    private modalService: SiModalService
  ) {
    this.switchTheme(this.currentTheme ?? 'auto');
    this.authService.initialize();
  }

  ngOnInit(): void {
    this.configService.getConf().subscribe({
      next: (data) => {
        this.version = data.app_version;
        this.updateHelpItems();
        this.cdr.markForCheck();
      },
      error: () => {
        this.version = 'N/A';
        this.updateHelpItems();
        this.cdr.markForCheck();
      },
    });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        if (this.authService.instance.getActiveAccount() !== null) {
          this.setLoginDisplay();
        } else {
          this.login();
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.SSO_SILENT_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);

        if (payload.accessToken) {
          this.secureTokenService.storeTokens(payload.accessToken, payload.idToken).subscribe({
            next: () => {
              // Tokens stored successfully.
            },
            error: (error) => {
              if (error.status !== 404) {
                console.error('Failed to store tokens securely:', error);
              }
            },
          });
        }

        this.userService.createLoginAuditLog(result.eventType.toString()).subscribe({
          error: () => {
            // Ignore audit log failures.
          },
        });

        this.setLoginDisplay();
      });
  }

  private updateHelpItems(): void {
    const askAiItem = this.isGlobalAdmin ? [{ title: 'AskAI', action: () => this.openAskAiDialog() }] : [];

    this.helpItems = [
      { title: 'Wiz Dashboard', href: 'https://siemens.com/wiz', target: '_blank' },
      { title: 'USS Documentation', href: 'https://clp.siemens.io/cnapp-service/how_tos/uss/', target: '_blank' },
      { title: 'API Documentation', href: 'https://clp.siemens.io/cnapp-service/how_tos/uss_api/', target: '_blank' },
      {
        title: 'Report Self Service Bug',
        href: 'https://clp.siemens.io/cnapp-service/contacts/service_management/#incident-management',
        target: '_blank',
      },
      ...askAiItem,
      { title: '-' },
      { title: 'Swagger & OpenAPI Doc', href: 'https://my-cloud-security.code.siemens.io/iam-integration/', target: '_blank' },
      { title: 'Version ' + this.version },
    ];
  }

  private hasGlobalAdminRole(tokenRoles: string[]): boolean {
    return tokenRoles.some((role) => {
      const normalizedRole = role.toLowerCase().replace(/[^a-z0-9]/g, '');
      return normalizedRole.includes('global') && normalizedRole.includes('admin');
    });
  }

  private extractWizEffectiveRoles(user: unknown): string[] {
    if (!user || typeof user !== 'object') {
      return [];
    }

    const source = user as Record<string, unknown>;
    const effectiveRoleObject = source['effectiveRole'] as Record<string, unknown> | undefined;
    const effectiveRoleId = typeof effectiveRoleObject?.['id'] === 'string' ? effectiveRoleObject['id'] : undefined;

    const rawRoles =
      source['wizEffectiveRoles'] ??
      source['WizEffectiveRoles'] ??
      source['wizEffectiveRole'] ??
      source['WizEffectiveRole'] ??
      source['effectiveRoleId'] ??
      source['EffectiveRoleId'] ??
      source['effectiveRole'] ??
      source['EffectiveRole'] ??
      effectiveRoleId;

    if (Array.isArray(rawRoles)) {
      return rawRoles
        .filter((role): role is string => typeof role === 'string')
        .map((role) => role.trim())
        .filter((role) => role.length > 0);
    }

    if (typeof rawRoles === 'string') {
      return rawRoles
        .split(/[;,]/)
        .map((role) => role.trim())
        .filter((role) => role.length > 0);
    }

    if (rawRoles && typeof rawRoles === 'object') {
      const roleObject = rawRoles as Record<string, unknown>;
      const roleCandidate = roleObject['id'] ?? roleObject['name'] ?? roleObject['value'];

      if (typeof roleCandidate === 'string' && roleCandidate.trim().length > 0) {
        return [roleCandidate.trim()];
      }
    }

    return [];
  }

  private extractTokenRoles(claims: Record<string, unknown> | undefined): string[] {
    if (!claims) {
      return [];
    }

    const candidateKeys = ['roles', 'role', 'app_roles', 'appRoles'];
    const roles: string[] = [];

    candidateKeys.forEach((key) => {
      const value = claims[key];

      if (Array.isArray(value)) {
        roles.push(...value.filter((item): item is string => typeof item === 'string'));
      } else if (typeof value === 'string') {
        roles.push(value);
      }
    });

    Object.entries(claims).forEach(([key, value]) => {
      const lowerKey = key.toLowerCase();
      if (lowerKey.endsWith('/role') || lowerKey.endsWith('/roles')) {
        if (Array.isArray(value)) {
          roles.push(...value.filter((item): item is string => typeof item === 'string'));
        } else if (typeof value === 'string') {
          roles.push(value);
        }
      }
    });

    return [...new Set(roles)];
  }

  private extractUserEmail(account: ReturnType<typeof this.authService.instance.getActiveAccount>): string {
    const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
    const claimCandidates = [claims?.['preferred_username'], claims?.['upn'], claims?.['email'], claims?.['unique_name']];

    for (const candidate of claimCandidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate.trim();
      }
    }

    const emailsClaim = claims?.['emails'];
    if (Array.isArray(emailsClaim)) {
      const firstEmail = emailsClaim.find(
        (value): value is string => typeof value === 'string' && value.trim().length > 0
      );
      if (firstEmail) {
        return firstEmail.trim();
      }
    }

    if (typeof account?.username === 'string' && account.username.trim().length > 0) {
      return account.username.trim();
    }

    return '';
  }

  login() {
    this.authService
      .ssoSilent({
        scopes: aadScopes,
        prompt: 'select_account',
      })
      .subscribe({
        next: (result) => {
          this.authService.instance.setActiveAccount(result.account);
          this.setLoginDisplay();
        },
        error: () => {
          this.authService.loginRedirect({
            scopes: aadScopes,
            prompt: 'select_account',
          });
        },
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  setLoginDisplay() {
    const account = this.authService.instance.getActiveAccount();
    const userEmail = this.extractUserEmail(account);
    const tokenRoles = this.extractTokenRoles(account?.idTokenClaims as Record<string, unknown> | undefined);
    const existingUser = this.configService.currentUser;
    const preservedEffectiveRoles = existingUser?.wizEffectiveRoles ?? (existingUser?.wizEffectiveRole ? [existingUser.wizEffectiveRole] : []);
    const tokenIsGlobalAdmin = this.hasGlobalAdminRole(tokenRoles);
    const storedRoles = preservedEffectiveRoles.length > 0 ? preservedEffectiveRoles : (existingUser?.roles ?? []);
    const storedIsGlobalAdmin = this.hasGlobalAdminRole(storedRoles);
    const wizRoles = tokenRoles.filter((role) => role.toLowerCase().includes('wiz'));
    const previousIsGlobalAdmin = this.isGlobalAdmin;
    this.isGlobalAdmin = previousIsGlobalAdmin || storedIsGlobalAdmin || tokenIsGlobalAdmin;

    this.userData = {
      id: String(account?.idTokenClaims?.['gid']),
      name: `${account?.idTokenClaims?.['preferred_given_name']} ${account?.idTokenClaims?.['preferred_family_name']}`,
      title: `${account?.idTokenClaims?.['preferred_given_name']} ${account?.idTokenClaims?.['preferred_family_name']}`,
      company: String(account?.idTokenClaims?.['org_code']),
      email: userEmail,
    };

    let currentUser = <User>{
      id: String(account?.idTokenClaims?.['gid'] ?? ''),
      email: userEmail,
      name: String(account?.name ?? ''),
      roles: tokenRoles,
      wizRoles,
    };

    if (preservedEffectiveRoles.length > 0) {
      const mergedRoles = [...new Set([...(currentUser.roles ?? []), ...preservedEffectiveRoles])];
      const mergedWizRoles = [...new Set([...(currentUser.wizRoles ?? []), ...preservedEffectiveRoles])];
      currentUser = {
        ...currentUser,
        roles: mergedRoles,
        wizRoles: mergedWizRoles,
        wizEffectiveRole: preservedEffectiveRoles[0],
        wizEffectiveRoles: preservedEffectiveRoles,
      };
    }

    this.configService.setUser(currentUser);

    if (currentUser.email && !this.samlUserFetched) {
      this.samlUserFetched = true;
      this.userService.getUser(currentUser.email).subscribe({
        next: (samlUser) => {
          const effectiveRoles = this.extractWizEffectiveRoles(samlUser);

          if (effectiveRoles.length > 0) {
            const mergedRoles = [...new Set([...(currentUser.roles ?? []), ...effectiveRoles])];
            const mergedWizRoles = [...new Set([...(currentUser.wizRoles ?? []), ...effectiveRoles])];

            currentUser = {
              ...currentUser,
              roles: mergedRoles,
              wizRoles: mergedWizRoles,
              wizEffectiveRole: effectiveRoles[0],
              wizEffectiveRoles: effectiveRoles,
            };

            this.isGlobalAdmin = this.hasGlobalAdminRole(effectiveRoles);
            this.configService.setUser(currentUser);
            this.updateHelpItems();
            this.cdr.markForCheck();
          }
        },
        error: () => {
          this.samlUserFetched = false;
        },
      });
    }

    this.updateHelpItems();
    this.loginDisplay = this.userData?.title !== '';
    this.cdr.markForCheck();
  }

  private switchTheme(theme: ThemeType) {
    this.themeService.applyThemeType(theme);
    this.themeItems = this.themeItems.map((item) => ({
      ...item,
      selected: item.id === theme,
    }));
    this.themeToggleQuickAction.icon = this.themeService.resolvedColorScheme === 'dark' ? 'element-sun' : 'element-economy';
  }

  private toggleTheme(): void {
    this.themeService.resolvedColorScheme === 'light' ? this.switchTheme('dark') : this.switchTheme('light');
  }

  private openAskAiDialog(): void {
    this.modalService.show(AskAiDialogComponent, {
      ignoreBackdropClick: true,
      keyboard: false,
      animated: true,
      class: 'modal-lg',
    });
  }

  themeToggleQuickAction = {
    icon: 'element-sun',
    action: () => this.toggleTheme(),
  };

  askAiQuickAction = {
    title: 'AskAI',
    icon: 'element-lightbulb',
    action: () => this.openAskAiDialog(),
  };
}
